import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";

import { useExitOnboardingMode } from "@/hooks/onboarding/useOnboarding";
import {
  selectIsOnboardingActive,
  selectOnboardingModuleCode
} from "@/store/slice/onboardingSlice";
import { setShowProductOverviewPanel } from "@/store/slice/uiSlice";

import { TourClickBlocker } from "./TourClickBlocker";
import { TourSpotlight } from "./TourSpotlight";
import { TourTooltip } from "./TourTooltip";

import { useTargetElement } from "./hooks/useTargetElement";
import { useTourStep } from "./hooks/useTourStep";

/**
 * The main onboarding tour component.
 */
export function OnboardingTour() {
  const isOnboardingActive = useSelector(selectIsOnboardingActive);
  const moduleCode = useSelector(selectOnboardingModuleCode);

  if (!isOnboardingActive || !moduleCode) return null;

  return <OnboardingTourSession key={moduleCode} />;
}

function OnboardingTourSession() {
  const dispatch = useDispatch();
  const exitOnboardingMode = useExitOnboardingMode();
  const {
    currentStep,
    stepIndex,
    totalSteps,
    isFirst,
    isLast,
    goToNext,
    goToPrev,
    skip
  } = useTourStep();

  const targetEl = useTargetElement(currentStep?.tourId);

  if (!currentStep || stepIndex === null) return null;

  const handleNext = () => {
    goToNext();
  };

  const handleFinish = async () => {
    let completed = false;

    try {
      await goToNext(); // writes status: completed
      completed = true;
    } finally {
      exitOnboardingMode();
      if (completed) dispatch(setShowProductOverviewPanel(true));
    }
  };

  const handleSkip = async () => {
    try {
      await skip(); // writes status: in_progress
    } finally {
      exitOnboardingMode();
    }
  };

  return createPortal(
    <>
      <TourClickBlocker />
      <TourSpotlight
        targetEl={targetEl}
        measurementKey={currentStep.id}
        targetPadding={currentStep.targetPadding}
        targetRadius={currentStep.targetRadius}
      />
      <TourTooltip
        targetEl={targetEl}
        step={currentStep}
        stepIndex={stepIndex}
        totalSteps={totalSteps}
        isFirst={isFirst}
        isLast={isLast}
        onNext={handleNext}
        onPrev={goToPrev}
        onSkip={handleSkip}
        onFinish={handleFinish}
      />
    </>,
    document.body
  );
}
